import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box, IconButton, Typography, Button, Card, CardContent,
  Chip, CircularProgress, Stack,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StoreIcon from '@mui/icons-material/Store';
import EventIcon from '@mui/icons-material/Event';
import QRScanner, { ScanResult } from '../components/QRScanner';
import apiService from '../services/api';
import liffService from '../services/liff';
import { useAuth } from '../auth/AuthContext';
import { MyApplicationDetail } from '../types';
import '../styles/AttendancePage.css';

interface ShiftInfo {
  work_attendance_id: string;
  job_title: string | null;
  job_type: string | null;
  planned_start_time: string | null;
  planned_end_time: string | null;
  start_time: string | null;
  end_time: string | null;
  shift_date: string | null;
  salary_type: string | null;
  salary_amount: number | null;
  hourly_wage: number | null;
  actual_check_in_time: string | null;
  actual_check_out_time: string | null;
  status: string | null;
}

const getActionLabel = (actionType: 'check_in' | 'check_out'): string => (
  actionType === 'check_in' ? '出勤' : '退勤'
);

const mapScanErrorMessage = (rawError: string | undefined, actionType: 'check_in' | 'check_out'): string => {
  if (!rawError) return `${getActionLabel(actionType)}に失敗しました。`;
  const normalized = rawError.toLowerCase();
  if (normalized.includes('already checked in')) return '既に出勤済みです。';
  if (normalized.includes('already checked out')) return '既に退勤済みです。';
  if (normalized.includes('cannot check out without checking in first')) return '出勤してから退勤してください。';
  if (normalized.includes('invalid qr code format') || normalized.includes('invalid qr code secret') || normalized.includes('invalid or inactive store qr code')) return 'QRコードが無効です。正しいQRコードを読み込んでください。';
  if (normalized.includes('qr code has expired')) return 'QRコードの有効期限が切れています。管理者に確認してください。';
  if (normalized.includes('store_user_id is required') || normalized.includes('user_id is required')) return 'ユーザー情報の取得に失敗しました。再ログインしてください。';
  if (rawError.includes('この店舗に登録されているユーザーが見つかりません')) return 'この店舗のクルー登録がありません。勤務先店舗のQRコードを読み取ってください。';
  if (rawError.includes('本日の勤務予定が見つかりません') || rawError.includes('この応募は無効です')) return rawError;
  if (normalized.includes('failed to scan qr code') || normalized.includes('network') || normalized.includes('fetch')) return '通信またはシステムエラーが発生しました。時間をおいて再試行してください。';
  return rawError;
};

/**
 * LIFF URL形式のQRコードからバックエンド用の recrew://qr/ 形式に変換する。
 */
const normalizeQrCode = (raw: string): string => {
  // 内部形式: recrew://qr/{store_id}/{token}
  if (raw.startsWith('recrew://qr/')) {
    const parts = raw.replace('recrew://qr/', '').split('/');
    if (parts.length === 2 && parts[0] && parts[1]) return raw;
  }

  // LIFF URL形式: https://liff.line.me/{liff_id}?store-id={sid}&qr-token={token}
  try {
    const url = new URL(raw);
    const sid = url.searchParams.get('store-id');
    const token = url.searchParams.get('qr-token');
    if (sid && token) return `recrew://qr/${sid}/${token}`;
  } catch { /* invalid URL */ }

  throw new Error('無効なQRコードです。正しい店舗QRコードを読み込んでください。');
};

const todayJST = (): string => {
  const jst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 10);
};

const formatShiftDate = (yyyymmdd: string): string => {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return yyyymmdd;
  const date = new Date(y, m - 1, d);
  const weekday = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
  return `${m}月${d}日(${weekday})`;
};


type ShiftStatus = 'working' | 'not_started' | 'completed';

const getShiftStatus = (app: MyApplicationDetail): ShiftStatus => {
  if (app.actual_check_out_time) return 'completed';
  if (app.actual_check_in_time) return 'working';
  return 'not_started';
};

type PageMode = 'schedule' | 'scanning' | 'action';

const AttendancePage: React.FC = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const showBack = (location.state as { from?: string } | null)?.from === 'jobDetail';

  // QRコード経由のトークン（LIFFパラメータから取得）。
  // 下の effect で破棄するため、この画面が開いている間の値はマウント時に固定する
  // （毎レンダー読み直すと破棄後に isQrEntry が false へ変わり、QR直接起動なのに
  // 「QRを読み直す」が出るなど表示が途中で変わってしまう）。読み取りのみなので
  // StrictMode で初期化子が2回呼ばれても結果は同じ。
  const [qrEntry] = useState(() => ({
    qrToken: liffService.getQrToken(),
    storeId: liffService.getStoreId(),
  }));
  const { qrToken, storeId } = qrEntry;
  const isQrEntry = !!(qrToken && storeId);

  // QR起動1回につき1度だけ「出勤・退勤」画面に入るよう、受け取った時点で破棄する。
  // sessionStorage に残った qr-token を毎回読むと、一度でも店舗QRから起動した
  // セッションでは下部メニューの「出勤予定表」を開いても常に出勤・退勤画面が出てしまい、
  // 予定表に到達できなくなる（別店舗のQRで起動した後はその店舗の画面が出続ける）。
  // 破棄は描画後（effect）に行う。render 中に消費すると StrictMode の二重レンダリングで
  // 2回目に空になり、QRから起動したのに予定表が出てしまう。
  useEffect(() => {
    liffService.consumeQrToken();
  }, []);

  // 開発用モック: ?mock&store_id=<値> または ?mock_qr=<LIFF URL> でモックQR
  const mockQrCode = searchParams.get('mock_qr')
    ?? (searchParams.has('mock')
      ? `https://liff.line.me/mock-liff-id/?store-id=${searchParams.get('store_id') ?? '00000000-0000-0000-0000-000000000000'}&qr-token=mock-secret`
      : undefined);

  // ページモード:
  //   schedule  → 出勤予定表
  //   scanning  → カメラでQR読み取り中
  //   action    → QR読み取り済み、出勤/退勤を選択
  //   (LIFF QR経由の場合は最初から action、求人詳細経由の場合は最初から scanning)
  const [pageMode, setPageMode] = useState<PageMode>(
    isQrEntry ? 'action' : showBack ? 'scanning' : 'schedule'
  );
  const [scannedQrPayload, setScannedQrPayload] = useState<string>(
    isQrEntry ? `recrew://qr/${storeId}/${qrToken}` : ''
  );
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [shiftInfoList, setShiftInfoList] = useState<ShiftInfo[]>([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState<boolean>(false);
  // 読み取った店舗にクルー登録が無い（＝他店舗のQR）場合に true。
  const [isUnregisteredStore, setIsUnregisteredStore] = useState<boolean>(false);

  // 出勤予定表
  const [scheduleList, setScheduleList] = useState<MyApplicationDetail[]>([]);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState<boolean>(false);

  const fetchSchedule = useCallback(async () => {
    if (!userId) return;
    setIsLoadingSchedule(true);
    try {
      const todayStr = todayJST();
      // 本日以降のシフトのみ取得
      const res = await apiService.getMyApplicationsWithDetails(userId, todayStr);
      if (res.success && res.data) {
        const statusPriority = (s: ShiftStatus) => (s === 'working' ? 0 : s === 'not_started' ? 1 : 2);
        setScheduleList(
          [...res.data].sort((a, b) => {
            const sa = getShiftStatus(a);
            const sb = getShiftStatus(b);
            if (sa !== sb) return statusPriority(sa) - statusPriority(sb);
            return a.shift_date.localeCompare(b.shift_date) || a.start_time.localeCompare(b.start_time);
          })
        );
      }
    } catch { /* エラー時は空リストのまま */ }
    setIsLoadingSchedule(false);
  }, [userId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    if (userId) localStorage.setItem('user_id', userId);
  }, [userId]);

  // ストアIDをQRペイロードから抽出
  const extractStoreId = (payload: string): string | null => {
    if (!payload) return null;
    // recrew://qr/{store_id}/{token}
    const match = payload.match(/^recrew:\/\/qr\/([^/]+)\//);
    return match ? match[1] : null;
  };

  // actionモードに入ったときに求人情報を取得
  useEffect(() => {
    if (pageMode !== 'action' || !userId) return;
    const sid = extractStoreId(scannedQrPayload);
    if (!sid) return;

    let cancelled = false;
    const fetchShifts = async () => {
      setIsLoadingShifts(true);
      try {
        const res = await apiService.getTodayShiftsWithStatus(sid, userId);
        if (!cancelled && res.success && res.data) {
          setShiftInfoList(Array.isArray(res.data.data) ? res.data.data : []);
          // registered が false のときは「予定なし」ではなく「この店舗の登録が無い」。
          // 他店舗のQRを読み取ったケースがこれに当たる（QR自体は有効）。
          const unregistered = res.data.registered === false;
          setIsUnregisteredStore(unregistered);
          if (unregistered) {
            const storeLabel = res.data.store_name ? `（店舗: ${res.data.store_name}）` : '';
            setError(`この店舗のクルー登録がありません。勤務先店舗のQRコードを読み取ってください。${storeLabel}`);
          }
        }
      } catch { /* シフト情報取得失敗でもブロックしない */ }
      if (!cancelled) setIsLoadingShifts(false);
    };
    fetchShifts();
    return () => { cancelled = true; };
  }, [pageMode, scannedQrPayload, userId]);

  // QRスキャナーからのコールバック: QR読み取り成功 → actionモードへ遷移
  const handleQrScanned = async (result: ScanResult): Promise<boolean> => {
    try {
      const payload = normalizeQrCode(result.qr_code);
      setError('');
      setScannedQrPayload(payload);
      setPageMode('action');
      return true;
    } catch (err: any) {
      setError(err.message || '無効なQRコードです。');
      return false;
    }
  };

  // 出勤/退勤ボタン押下 → API送信
  const handleAction = async (actionType: 'check_in' | 'check_out') => {
    if (!scannedQrPayload) return;
    try {
      setIsProcessing(true);
      setError('');
      setSuccessMessage('');
      const response = await apiService.post('/attendance/scan-qr', {
        qr_code: scannedQrPayload,
        action_type: actionType,
        user_id: userId || undefined,
        device_info: navigator.userAgent,
      });
      if (response.success) {
        setSuccessMessage(actionType === 'check_in' ? '出勤が記録されました。' : '退勤が記録されました。');
      } else {
        setError(mapScanErrorMessage(response.error, actionType));
      }
    } catch (err: any) {
      setError(mapScanErrorMessage(err?.message, actionType));
    } finally {
      setIsProcessing(false);
    }
  };

  // やり直し: スキャン画面に戻る
  const handleRescan = () => {
    setError('');
    setSuccessMessage('');
    setScannedQrPayload('');
    setShiftInfoList([]);
    setIsUnregisteredStore(false);
    setPageMode('scanning');
  };

  const handleLaunchScanner = () => {
    setError('');
    setSuccessMessage('');
    setScannedQrPayload('');
    setShiftInfoList([]);
    setIsUnregisteredStore(false);
    setPageMode('scanning');
  };

  const handleBackToSchedule = () => {
    setError('');
    setSuccessMessage('');
    setScannedQrPayload('');
    setShiftInfoList([]);
    setIsUnregisteredStore(false);
    setPageMode('schedule');
    fetchSchedule();
  };

  const today = todayJST();

  const pageTitles: Record<PageMode, string> = {
    schedule: '出勤予定表',
    scanning: '出退勤QR',
    action: '出勤・退勤',
  };

  return (
    <Box sx={{ p: 0 }}>

      {/* ページヘッダー */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1,
        pt: 1.5,
        pb: 0.5,
        minHeight: 48,
      }}>
        {pageMode === 'schedule' ? (
          showBack && (
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: '#3c3c3c' }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )
        ) : (
          <IconButton
            onClick={showBack ? () => navigate(-1) : handleBackToSchedule}
            size="small"
            sx={{ color: '#3c3c3c' }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        <Typography sx={{
          flex: 1,
          fontWeight: 700,
          fontSize: '16px',
          color: '#3c3c3c',
          pl: (pageMode === 'schedule' && !showBack) ? 1.5 : 0.5,
        }}>
          {pageTitles[pageMode]}
        </Typography>
      </Box>

      {/* コンテンツ */}
      <Box sx={{ px: 2 }}>

        {/* ── 出勤予定表モード ── */}
        {pageMode === 'schedule' && (
          <>
            {isLoadingSchedule ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                <CircularProgress />
              </Box>
            ) : scheduleList.length === 0 ? (
              <Box sx={{ textAlign: 'center', my: 6 }}>
                <Typography sx={{ fontSize: '14px', color: '#999', mb: 3 }}>
                  今日以降の出勤予定はありません
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleLaunchScanner}
                  sx={{
                    borderRadius: 3,
                    fontWeight: 700,
                    bgcolor: '#5856D6',
                    '&:hover': { bgcolor: '#4745B5' },
                  }}
                >
                  QRコードを読み取る
                </Button>
              </Box>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {scheduleList.map((app) => {
                  const shiftStatus = getShiftStatus(app);
                  const isCompleted = shiftStatus === 'completed';
                  const isWorking = shiftStatus === 'working';
                  const isToday = app.shift_date === today;
                  return (
                    <Card
                      key={app.id}
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: isCompleted ? '#e0e0e0' : isToday ? '#5856D6' : '#e0e0e0',
                        borderWidth: !isCompleted && isToday ? 2 : 1,
                        bgcolor: isCompleted ? '#f5f5f5' : 'white',
                      }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>

                        {/* 日付 + バッジ */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <EventIcon sx={{ fontSize: 17, color: isCompleted ? '#bbb' : isToday ? '#5856D6' : '#888' }} />
                          <Typography sx={{
                            fontSize: '14px',
                            fontWeight: !isCompleted && isToday ? 700 : 400,
                            color: isCompleted ? '#aaa' : isToday ? '#5856D6' : '#555',
                          }}>
                            {formatShiftDate(app.shift_date)}
                          </Typography>
                          {isCompleted && (
                            <Chip
                              label="完了"
                              size="small"
                              sx={{ bgcolor: '#e0e0e0', color: '#888', fontWeight: 700, fontSize: '11px', height: 20 }}
                            />
                          )}
                          {!isCompleted && isWorking && (
                            <Chip
                              label="出勤中"
                              size="small"
                              sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '11px', height: 20 }}
                            />
                          )}
                          {!isCompleted && !isWorking && isToday && (
                            <Chip
                              label="本日"
                              size="small"
                              sx={{ bgcolor: '#5856D6', color: '#fff', fontWeight: 700, fontSize: '11px', height: 20 }}
                            />
                          )}
                        </Box>

                        {/* 店舗名 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <StoreIcon sx={{ fontSize: 17, color: isCompleted ? '#bbb' : '#888' }} />
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: isCompleted ? '#aaa' : '#3c3c3c' }}>
                            {app.store_name}
                          </Typography>
                        </Box>

                        {/* 時間 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isCompleted ? 0 : 1.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 17, color: isCompleted ? '#bbb' : '#888' }} />
                          <Typography sx={{ fontSize: '14px', color: isCompleted ? '#aaa' : '#555' }}>
                            {app.start_time} 〜 {app.end_time}
                          </Typography>
                        </Box>

                        {/* QR起動ボタン（完了時は非表示） */}
                        {!isCompleted && (
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleLaunchScanner}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 700,
                              fontSize: '14px',
                              bgcolor: '#5856D6',
                              py: 1,
                              '&:hover': { bgcolor: '#4745B5' },
                            }}
                          >
                            出退勤QRを起動
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}

              </Stack>
            )}
          </>
        )}

        {/* ── QRスキャンモード ── */}
        {pageMode === 'scanning' && (
          <>
            {error && <div className="alert alert-error">{error}</div>}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', color: '#666' }}>
                店舗のQRコードを読み取ってください
              </Typography>
              <div className="scanner-card">
                <QRScanner
                  onScan={handleQrScanned}
                  isActive={!isProcessing}
                  facingMode="environment"
                  autoClearTime={0}
                  autoAction="check_in"
                  mockQrCode={mockQrCode}
                />
              </div>
            </Box>
            <div className="info-card">
              <h3>ご注意</h3>
              <ul className="info-list">
                <li>退勤を記録し、マネージャーの確認後に勤怠が承認されます。</li>
              </ul>
            </div>
          </>
        )}

        {/* ── 出勤/退勤選択モード ── */}
        {pageMode === 'action' && (
          <>
            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {successMessage ? (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={showBack ? () => navigate(-1) : handleBackToSchedule}
                    sx={{
                      mt: 1,
                      borderColor: '#5856D6',
                      color: '#5856D6',
                      borderRadius: 3,
                      '&:hover': { borderColor: '#4745B5', color: '#4745B5' },
                    }}
                  >
                    {showBack ? '戻る' : '予定表に戻る'}
                  </Button>
                </Box>
                <div className="info-card" style={{ textAlign: 'left' }}>
                  <h3>ご注意</h3>
                  <ul className="info-list">
                    <li>退勤を記録し、マネージャーの確認後に勤怠が承認されます。</li>
                  </ul>
                </div>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>

                {/* 本日の勤務予定カード */}
                {isLoadingShifts ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CircularProgress size={28} sx={{ color: '#5856D6' }} />
                  </Box>
                ) : shiftInfoList.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#888', mb: 1 }}>
                      本日の勤務予定
                    </Typography>
                    {shiftInfoList.map((shift) => (
                      <Card
                        key={shift.work_attendance_id}
                        variant="outlined"
                        sx={{ mb: 1.5, borderRadius: 3, borderColor: '#e0e0e0' }}
                      >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#3c3c3c' }}>
                              {shift.job_title || '求人情報'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: '#888' }} />
                            <Typography sx={{ fontSize: '14px', color: '#555' }}>
                              {shift.start_time || shift.planned_start_time || '--:--'}
                              {' 〜 '}
                              {shift.end_time || shift.planned_end_time || '--:--'}
                            </Typography>
                          </Box>
                          {shift.shift_date && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 16, color: '#888' }} />
                              <Typography sx={{ fontSize: '13px', color: '#777' }}>
                                {shift.shift_date}
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {shift.salary_amount != null && (
                              <Chip
                                label={`¥${shift.salary_amount.toLocaleString()}${shift.salary_type === 'hourly' ? '/時' : shift.salary_type === 'daily' ? '/日' : ''}`}
                                size="small"
                                sx={{ bgcolor: '#f0effe', color: '#5856D6', fontWeight: 600, fontSize: '12px' }}
                              />
                            )}
                            {shift.actual_check_in_time && !shift.actual_check_out_time && (
                              <Chip label="出勤中" size="small" color="success" variant="outlined" sx={{ fontSize: '12px' }} />
                            )}
                            {shift.actual_check_out_time && (
                              <Chip label="退勤済" size="small" color="default" variant="outlined" sx={{ fontSize: '12px' }} />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2, mb: 2 }}>
                    <Typography sx={{ fontSize: '14px', color: '#999' }}>
                      {isUnregisteredStore
                        ? 'この店舗のクルー登録がありません'
                        : '本日の勤務予定はありません'}
                    </Typography>
                  </Box>
                )}

                {(() => {
                  const isWorking = !isLoadingShifts && shiftInfoList.some((s: ShiftInfo) => s.actual_check_in_time && !s.actual_check_out_time);
                  const actionType: 'check_in' | 'check_out' = isWorking ? 'check_out' : 'check_in';
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={actionType === 'check_in' ? <LoginIcon /> : <LogoutIcon />}
                        onClick={() => handleAction(actionType)}
                        // 未登録店舗のQRでは打刻できない（押しても404になるだけなので押させない）
                        disabled={isProcessing || isLoadingShifts || isUnregisteredStore}
                        sx={{
                          py: 2,
                          fontSize: '18px',
                          fontWeight: 700,
                          borderRadius: 3,
                          bgcolor: actionType === 'check_in' ? '#5856D6' : '#c62828',
                          '&:hover': { bgcolor: actionType === 'check_in' ? '#4745B5' : '#b71c1c' },
                        }}
                      >
                        {actionType === 'check_in' ? '出勤する' : '退勤する'}
                      </Button>
                    </Box>
                  );
                })()}
                {/* 未登録店舗のQRを読んでしまったときは、QR直接起動でも読み直せる導線を出す */}
                {(!isQrEntry || isUnregisteredStore) && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      variant="text"
                      onClick={handleRescan}
                      disabled={isProcessing}
                      sx={{ color: '#666' }}
                    >
                      QRを読み直す
                    </Button>
                  </Box>
                )}
                <div className="info-card" style={{ textAlign: 'left' }}>
                  <h3>ご注意</h3>
                  <ul className="info-list">
                    <li>退勤を記録し、マネージャーの確認後に勤怠が承認されます。</li>
                  </ul>
                </div>
              </Box>
            )}
          </>
        )}

      </Box>
    </Box>
  );
};

export default AttendancePage;
